import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Bell, PlayCircle, Search, ChevronDown, Menu, Grid3x3, Settings, 
  FileText, CreditCard, Archive, ArrowRightLeft, Check,
  User, LogOut, Sun, Moon, Users, Globe
} from './Icons';
import { useTranslation } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';

interface TopbarProps {
  toggleSidebar: () => void;
  collapsed: boolean;
}

const Topbar: React.FC<TopbarProps> = ({ toggleSidebar, collapsed }) => {
  const [isAppSwitcherOpen, setIsAppSwitcherOpen] = useState(false);
  const [isTenantDropdownOpen, setIsTenantDropdownOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [currentTenant, setCurrentTenant] = useState('iomarket AG');
  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      return document.documentElement.classList.contains('dark') || 
             localStorage.getItem('theme') === 'dark';
    }
    return false;
  });
  
  const { language, setLanguage, t } = useTranslation();
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const switcherRef = useRef<HTMLDivElement>(null);
  const profileRef = useRef<HTMLDivElement>(null);
  const langRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDarkMode]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (switcherRef.current && !switcherRef.current.contains(event.target as Node)) {
        setIsAppSwitcherOpen(false);
        setIsTenantDropdownOpen(false);
      }
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setIsProfileOpen(false);
      }
      if (langRef.current && !langRef.current.contains(event.target as Node)) {
        setIsLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const apps = [
    { name: 'Invoice Workflow', icon: FileText, color: 'bg-brand-medium', iconColor: 'text-white' },
    { name: 'Purchase-to-Pay', icon: CreditCard, color: 'bg-teal-600', iconColor: 'text-white' },
    { name: 'Archive', icon: Archive, color: 'bg-amber-700', iconColor: 'text-white' },
    { name: 'Compare', icon: ArrowRightLeft, color: 'bg-indigo-600', iconColor: 'text-white' },
  ];

  const handleSignOut = () => {
    logout();
    navigate('/login');
  };

  return (
    <header className="h-16 bg-white dark:bg-brand-card-dark border-b border-gray-200 dark:border-white/5 flex items-center justify-between px-4 sticky top-0 z-[60] transition-colors duration-300">
      <div className="flex items-center flex-1 gap-2">
        <button onClick={toggleSidebar} className="p-2 hover:bg-gray-100 dark:hover:bg-white/10 rounded-lg text-brand-dark dark:text-gray-300">
           <span className="sr-only">Toggle Sidebar</span>
           <Menu size={20} strokeWidth={2.5} />
        </button>

        <div className="relative w-full max-w-md hidden md:block">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-medium dark:text-brand-bright" size={16} strokeWidth={2.5} />
          <input 
            type="text" 
            placeholder={t('search_resources')} 
            className="w-full pl-9 pr-4 py-2 bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-lg text-sm font-bold focus:bg-white dark:focus:bg-white/5 focus:outline-none focus:ring-2 focus:ring-brand-medium/30 focus:border-brand-medium transition-all placeholder-brand-dark/40 dark:placeholder-gray-600 text-brand-dark dark:text-gray-100"
          />
        </div>
      </div>

      <div className="flex items-center space-x-3 shrink-0">
        <div className="hidden lg:flex items-center space-x-3 mr-4">
           <button className="flex items-center px-4 py-1.5 text-[11px] font-black uppercase tracking-widest text-blue-700 bg-blue-50 dark:bg-blue-900/20 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-full transition-colors border border-blue-200 dark:border-blue-800/30">
            <PlayCircle size={14} className="mr-1.5 fill-blue-600 dark:fill-blue-400 text-white dark:text-brand-surface-dark" />
            {t('product_tour')}
          </button>
          
          <button className="flex items-center px-4 py-1.5 text-[11px] font-black uppercase tracking-widest text-red-700 bg-red-50 dark:bg-red-900/20 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-full transition-colors border border-red-200 dark:border-blue-800/30">
            <PlayCircle size={14} className="mr-1.5 fill-red-600 dark:fill-red-400 text-white dark:text-brand-surface-dark" />
            {t('tutorial')}
          </button>
        </div>

        <button 
          onClick={() => setIsDarkMode(!isDarkMode)}
          className="p-2 text-brand-medium dark:text-brand-bright hover:bg-gray-100 dark:hover:bg-white/10 rounded-lg transition-colors"
        >
          {isDarkMode ? <Sun size={20} className="text-amber-400" strokeWidth={2.5} /> : <Moon size={20} className="text-brand-dark" strokeWidth={2.5} />}
        </button>

        <button className="relative p-2 text-brand-medium dark:text-brand-bright hover:bg-gray-100 dark:hover:bg-white/10 rounded-lg">
          <Bell size={20} strokeWidth={2.5} />
          <span className="absolute top-1 right-1 h-4 w-4 bg-red-600 text-white text-[9px] font-black flex items-center justify-center rounded-full border-2 border-white dark:border-brand-card-dark">9</span>
        </button>

        {/* Language Picker */}
        <div className="relative" ref={langRef}>
          <div 
            onClick={() => setIsLangOpen(!isLangOpen)}
            className="hidden sm:flex items-center space-x-1 text-sm font-bold text-brand-dark dark:text-gray-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-white/10 px-3 py-1.5 rounded-lg border border-transparent hover:border-gray-200 dark:hover:border-white/10 transition-all"
          >
            <img src={language === 'en' ? "https://flagcdn.com/w20/gb.png" : "https://flagcdn.com/w20/de.png"} alt={language} className="w-5 rounded-[2px]" />
            <span className="text-xs uppercase tracking-widest">{language === 'en' ? 'EN' : 'DE'}</span>
            <ChevronDown size={12} strokeWidth={3} className={`transition-transform ${isLangOpen ? 'rotate-180' : ''}`} />
          </div>

          {isLangOpen && (
            <div className="absolute top-full right-0 mt-2 bg-white dark:bg-brand-card-dark border border-gray-200 dark:border-white/10 rounded-lg shadow-xl z-[100] overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200 min-w-[120px]">
              <button 
                onClick={() => { setLanguage('en'); setIsLangOpen(false); }}
                className={`w-full flex items-center gap-3 px-4 py-3 text-xs font-black uppercase tracking-widest transition-colors ${language === 'en' ? 'bg-brand-light dark:bg-white/10 text-brand-dark' : 'text-gray-500 hover:bg-gray-50 dark:hover:bg-white/5'}`}
              >
                <img src="https://flagcdn.com/w20/gb.png" alt="EN" className="w-5 rounded-[2px]" /> English
              </button>
              <button 
                onClick={() => { setLanguage('de'); setIsLangOpen(false); }}
                className={`w-full flex items-center gap-3 px-4 py-3 text-xs font-black uppercase tracking-widest transition-colors ${language === 'de' ? 'bg-brand-light dark:bg-white/10 text-brand-dark' : 'text-gray-500 hover:bg-gray-50 dark:hover:bg-white/5'}`}
              >
                <img src="https://flagcdn.com/w20/de.png" alt="DE" className="w-5 rounded-[2px]" /> Deutsch
              </button>
            </div>
          )}
        </div>

        <div className="flex items-center space-x-1 pl-4 border-l border-gray-200 dark:border-white/10 relative">
           <button 
             onClick={() => navigate('/settings/general')}
             className="p-2 text-brand-medium dark:text-brand-bright hover:bg-gray-100 dark:hover:bg-white/10 rounded-lg group transition-all" 
           >
             <Settings size={20} className="group-hover:rotate-90 transition-transform duration-500" strokeWidth={2.5} />
           </button>
        </div>

        <div className="relative" ref={switcherRef}>
          <button 
             onClick={() => setIsAppSwitcherOpen(!isAppSwitcherOpen)}
             className={`p-2 rounded-lg transition-colors ${isAppSwitcherOpen ? 'bg-brand-medium/10 text-brand-dark dark:text-brand-bright' : 'text-brand-medium dark:text-brand-bright hover:bg-gray-100 dark:hover:bg-white/10'}`} 
           >
             <Grid3x3 size={20} strokeWidth={2.5} />
           </button>

           {isAppSwitcherOpen && (
             <div className="absolute top-full right-0 mt-3 w-80 bg-white dark:bg-brand-card-dark text-brand-dark dark:text-gray-200 rounded-xl shadow-2xl z-[100] overflow-hidden border border-gray-200 dark:border-white/10 animate-in fade-in zoom-in-95 duration-200 origin-top-right">
                <div className="relative">
                  <div onClick={() => setIsTenantDropdownOpen(!isTenantDropdownOpen)} className="px-5 py-4 flex items-center justify-between border-b border-gray-100 dark:border-white/5 hover:bg-gray-50 dark:hover:bg-white/5 cursor-pointer group transition-colors">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-brand-dark rounded-lg flex items-center justify-center text-xs font-black text-white">IO</div>
                        <div className="flex flex-col">
                          <span className="text-sm font-black tracking-tight text-gray-900 dark:text-white">{currentTenant}</span>
                          <span className="text-[10px] text-brand-medium font-bold uppercase tracking-widest">Enterprise Platform</span>
                        </div>
                    </div>
                    <ChevronDown size={18} className={`text-brand-medium transition-transform ${isTenantDropdownOpen ? 'rotate-180' : ''}`} strokeWidth={3} />
                  </div>
                </div>

                <div className="py-2">
                   <div className="px-5 py-2 text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 border-b border-gray-50 dark:border-white/5 mb-1">Applications</div>
                   <div className="max-h-[400px] overflow-y-auto custom-scrollbar">
                      {apps.map((app) => (
                        <button key={app.name} className="w-full flex items-center gap-4 px-5 py-3 hover:bg-brand-light/30 dark:hover:bg-white/10 transition-colors group">
                          <div className={`w-10 h-10 ${app.color} rounded-lg flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform border border-black/5`}>
                             <app.icon size={20} className={app.iconColor} strokeWidth={2.5} />
                          </div>
                          <span className="text-sm font-bold tracking-tight text-gray-800 dark:text-gray-200 group-hover:text-brand-dark dark:group-hover:text-white">{app.name}</span>
                        </button>
                      ))}
                   </div>
                </div>
             </div>
           )}
        </div>

        <div className="relative" ref={profileRef}>
          <div 
            onClick={() => setIsProfileOpen(!isProfileOpen)}
            className={`flex items-center space-x-2 pl-4 cursor-pointer border-l border-gray-200 dark:border-white/10 ml-2 group transition-colors py-1 pr-1 rounded-r-xl ${isProfileOpen ? 'bg-brand-light dark:bg-white/10' : 'hover:bg-brand-light/40 dark:hover:bg-white/5'}`} 
          >
            <div className="w-8 h-8 rounded-full bg-[#00A3C4] border-2 border-white dark:border-brand-card-dark flex items-center justify-center text-white font-black text-xs shrink-0 group-hover:scale-110 transition-transform">
              {user?.name.split(' ').map(n => n[0]).join('') || 'DK'}
            </div>
            {!collapsed && <ChevronDown size={14} className="text-brand-medium" strokeWidth={3} />}
          </div>

          {isProfileOpen && (
            <div className="absolute top-full right-0 mt-3 w-80 bg-white dark:bg-brand-card-dark rounded-xl shadow-2xl z-[100] border border-gray-200 dark:border-white/10 animate-in fade-in zoom-in-95 duration-200 origin-top-right">
              <div className="bg-brand-light/30 dark:bg-white/5 px-6 py-6 border-b border-gray-100 dark:border-white/5 flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-[#00A3C4] border-4 border-white dark:border-brand-card-dark flex items-center justify-center text-white font-black text-2xl shadow-lg">
                  {user?.name.split(' ').map(n => n[0]).join('') || 'DK'}
                </div>
                <div className="flex flex-col">
                  <span className="text-xl font-black text-brand-dark dark:text-white tracking-tight leading-none mb-1">{user?.name || 'Daniel Kohler'}</span>
                  <span className="text-xs text-brand-medium dark:text-brand-bright font-bold">{user?.email || 'd.kohler@io-market.com'}</span>
                </div>
              </div>
              <div className="py-2">
                <button 
                  onClick={() => {
                    navigate('/settings/general');
                    setIsProfileOpen(false);
                  }}
                  className="w-full flex items-center gap-4 px-6 py-4 hover:bg-brand-light/30 dark:hover:bg-white/10 transition-colors group text-left"
                >
                  <User size={18} className="text-brand-medium dark:text-brand-bright" strokeWidth={2.5} />
                  <span className="text-sm font-bold text-brand-dark dark:text-gray-200">{t('profile_settings')}</span>
                </button>
                <button 
                  onClick={handleSignOut}
                  className="w-full flex items-center gap-4 px-6 py-4 hover:bg-red-50 dark:hover:bg-red-950/20 transition-colors group text-left"
                >
                  <LogOut size={18} className="text-red-600" strokeWidth={2.5} />
                  <span className="text-sm font-bold text-red-700 dark:text-red-400">{t('sign_out')}</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Topbar;